import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArchiveRestore, CheckCircle, ChevronRight, Plus, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { KgValue } from '@/components/efs/KgValue';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { QueryFailureNotice } from '@/features/audit-issues/QueryFailureNotice';

import { useInboundLotsList, useInboundLot, useConfirmInboundLot } from './useInbound';
import type { InboundLotDto } from './types';
import { InboundCreateDialog } from './InboundCreateDialog';
import { DeleteInboundLotDialog } from './DeleteInboundLotDialog';
import { DeletedInboundLotsDialog } from './DeletedInboundLotsDialog';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];
const NS = 'operations';

const StatusBadge = ({ status }: { status: string }) => {
  const { t } = useTranslation(NS);
  const variantMap: Record<string, 'default' | 'secondary' | 'outline'> = {
    confirmed: 'default',
    imported: 'secondary',
    locked: 'outline',
  };
  return (
    <Badge variant={variantMap[status] ?? 'secondary'}>
      {t(`efs.operations.statuses.${status}`, { defaultValue: status })}
    </Badge>
  );
};

const InboundLotDrawer = ({
  lotId,
  open,
  onOpenChange,
}: {
  lotId: string | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const { t } = useTranslation(NS);
  const query = useInboundLot(lotId ?? undefined);
  const confirm = useConfirmInboundLot();

  const lot = query.data;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('efs.operations.inbound.drawer.title')}</SheetTitle>
        </SheetHeader>

        {query.isLoading ? (
          <p className="mt-6 text-sm text-efs-text-muted">{t('efs.common.messages.loading')}</p>
        ) : lot ? (
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-sm font-semibold">{lot.lot_code}</p>
                <p className="text-xs text-efs-text-muted">{lot.inbound_date}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={lot.status} />
                {lot.status === 'imported' ? (
                  <Button
                    size="sm"
                    onClick={() => confirm.mutate(lot.inbound_lot_id)}
                    disabled={confirm.isPending}
                  >
                    <CheckCircle className="mr-1 h-4 w-4" />
                    {t('efs.operations.inbound.actions.confirm')}
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="rounded-md border border-efs-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('efs.operations.inbound.drawer.fields.raw_name')}</TableHead>
                    <TableHead>{t('efs.operations.inbound.drawer.fields.package_style')}</TableHead>
                    <TableHead className="text-right">
                      {t('efs.operations.inbound.drawer.fields.kg')}
                    </TableHead>
                    <TableHead>{t('efs.operations.inbound.drawer.fields.notes')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lot.lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-6 text-center text-efs-text-muted">
                        {t('efs.common.messages.empty')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    lot.lines.map((line) => (
                      <TableRow key={line.inbound_line_id}>
                        <TableCell className="font-mono text-xs">
                          {line.raw_incoming_item_name}
                        </TableCell>
                        <TableCell>{line.package_style ?? '—'}</TableCell>
                        <TableCell className="text-right">
                          <KgValue value={line.kg} />
                        </TableCell>
                        <TableCell className="text-xs text-efs-text-muted">
                          {line.notes ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};

export const InboundListPage = () => {
  const { t } = useTranslation(NS);
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [selectedLotId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<InboundLotDto | null>(null);
  const [deletedOpen, setDeletedOpen] = useState(false);

  const currentUser = useCurrentUser();
  const canCreate = hasPermission(currentUser, 'inbound.manage');
  const canManageInbound = hasPermission(currentUser, 'inbound.manage');
  const canReturnLinkedDispatch = hasPermission(currentUser, 'dispatch.request.fulfill');

  const query = useInboundLotsList(batchId, { page, per_page: perPage });

  const rows = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  const columns = useMemo<MasterTableColumn<InboundLotDto>[]>(
    () => [
      {
        key: 'lot_code',
        header: t('efs.operations.inbound.fields.lot_code'),
        cell: (row) => <span className="font-mono text-xs">{row.lot_code}</span>,
      },
      {
        key: 'inbound_date',
        header: t('efs.operations.inbound.fields.inbound_date'),
        cell: (row) => row.inbound_date,
      },
      {
        key: 'total_kg',
        header: t('efs.operations.inbound.fields.total_kg'),
        align: 'right',
        cell: (row) => <KgValue value={row.total_kg} />,
      },
      {
        key: 'status',
        header: t('efs.operations.inbound.fields.status'),
        cell: (row) => <StatusBadge status={row.status} />,
      },
      {
        key: 'open',
        header: '',
        align: 'right',
        cell: (row) => (
          <div className="flex justify-end gap-1">
            {canManageInbound && (row.status === 'imported' || row.status === 'confirmed') ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-destructive hover:text-destructive"
                aria-label={t('efs.operations.inbound.lot_delete.action', {
                  defaultValue: '入荷を取消',
                })}
                title={
                  row.linked_dispatch_request_id && !canReturnLinkedDispatch
                    ? t('efs.operations.inbound.lot_delete.dispatch_permission_hint', {
                        defaultValue: '配車へ戻す権限がありません。',
                      })
                    : t('efs.operations.inbound.lot_delete.action', {
                        defaultValue: '入荷を取消',
                      })
                }
                disabled={!!row.linked_dispatch_request_id && !canReturnLinkedDispatch}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(row);
                }}
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('efs.operations.inbound.lot_detail.title')}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/batches/${batchId}/inbound/${row.inbound_lot_id}`);
              }}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [t, batchId, navigate, canManageInbound, canReturnLinkedDispatch],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.operations.inbound.title')}
        subtitle={t('efs.operations.inbound.subtitle')}
        count={total}
        actions={
          canManageInbound ? (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => setDeletedOpen(true)}>
                <ArchiveRestore className="mr-1 h-4 w-4" />
                {t('efs.operations.inbound.actions.recently_deleted', {
                  defaultValue: '最近削除',
                })}
              </Button>
              {batchId && canCreate ? (
                <Button onClick={() => setCreateOpen(true)}>
                  <Plus className="mr-1 h-4 w-4" />
                  {t('efs.operations.inbound.actions.create')}
                </Button>
              ) : null}
            </div>
          ) : null
        }
      />
      <TableToolbar count={t('efs.masters.toolbar.count', { ns: 'masters', count: total })} />
      {query.isError ? (
        <QueryFailureNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <MasterTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.inbound_lot_id}
          isLoading={query.isLoading}
          pagination={{
            page,
            perPage,
            total,
            onPageChange: setPage,
            onPerPageChange: (nextPerPage) => {
              setPage(1);
              setPerPage(nextPerPage);
            },
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
        />
      )}

      <InboundLotDrawer lotId={selectedLotId} open={drawerOpen} onOpenChange={setDrawerOpen} />

      {batchId && createOpen ? (
        <InboundCreateDialog batchId={batchId} open={createOpen} onOpenChange={setCreateOpen} />
      ) : null}

      <DeleteInboundLotDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        lot={deleteTarget}
      />

      {batchId ? (
        <DeletedInboundLotsDialog
          batchId={batchId}
          open={deletedOpen}
          onOpenChange={setDeletedOpen}
          canRestoreLinkedDispatch={canReturnLinkedDispatch}
        />
      ) : null}
    </div>
  );
};

export default InboundListPage;
